import type { RemoveIndexSignature } from 'src/types/removeIndexSignature';

import { computed, watch } from 'vue';
import { ComposerTranslation, LocaleMessageValue, VueMessageType } from 'vue-i18n';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import axios from 'axios';

import { LocalNotifications } from '@capacitor/local-notifications';
import { simpleObservableStore } from 'boot/simpleObservableStore';
import { PhoneCallGatewayFactory } from 'components/phoneCall/phoneCallGateway/PhoneCallGatewayFactory';
import { loadingStatePlugin } from 'src/stores/_infrastructure/loadingStatePlugin';
import { useAlarmStore } from 'src/stores/alarm';
import { useAppStore } from 'src/stores/app';
import { initializeEventBusSubscriptions } from 'src/stores/app/eventSubscriptions';
import { useAuthenticationStore } from 'src/stores/authentication';
import { useOrganizationStore } from 'src/stores/organization';
import { usePhoneCallStore } from 'src/stores/phoneCall';
import { useSearchHistoryStore } from 'src/stores/searchHistory';
import { useUserStore } from 'src/stores/user';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import { useUserOnlineListStore } from 'src/stores/userOnlineList';
import { useUserStateStore } from 'src/stores/userState';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { OutgoingCallConfigurationForTheUser } from 'src/models/phoneCall';
import { User } from 'src/models/user';

import { FcmService } from 'src/services/fcm/fcm.service';

import { CommandBus } from 'src/util/command/CommandBus';
import { formatDate } from 'src/util/date';
import { EventBus } from 'src/util/eventBus/EventBus';
import { getBrowserName, getPlatformName } from 'src/util/platform';
import { isRunningInNativeCapacitorApp } from 'src/util/platform/browserInteractionCapabilities';
import { QueryStore } from 'src/util/query/QueryStore';
import { applyDarkMode } from 'src/util/theme/applyDarkMode';

type IntervalHandle = ReturnType<typeof setInterval>;

export class AppService {
	protected alarmFetchInterval: IntervalHandle | undefined;
	protected readonly commandBus: CommandBus;
	protected readonly eventBus: EventBus;
	protected fcmService: FcmService | undefined;
	initializeTheAuthenticatedUser = async () => {
		const authenticationStore = useAuthenticationStore();
		const userStore = useUserStore();
		const user = computed((): User | null => userStore.authenticatedUserInformation);

		watch(
			() => user.value,
			(user) => {
				if (user) {
					authenticationStore.loginWithUsernameAndPasswordSuccess();
				}
			}
		);

		await userStore.fetchAuthenticatedUserInformation({
			queryStore: this.queryStore,
		});

		await userStore.fetchUserConfigurationAndEffectiveRights({
			queryStore: this.queryStore,
		});
	};
	protected readonly queryStore: QueryStore;
	protected readonly route: RouteLocationNormalizedLoaded;
	protected readonly router: Router;
	protected splitViewEnabled: boolean;
	protected outgoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser | null | undefined;

	protected readonly runtimeStore: RuntimeStoreContext;
	protected readonly t: ComposerTranslation<
		{
			[p: string]: LocaleMessageValue<VueMessageType>;
		},
		string,
		RemoveIndexSignature<{
			[p: string]: LocaleMessageValue<VueMessageType>;
		}>,
		never,
		string,
		string
	>;
	protected updateTimeoutInterval: IntervalHandle = setInterval(() => {
		this.updateTheServiceWorkerRegistration();
	}, 1000 * 60);

	protected readonly window: Window;

	constructor(
		t: ComposerTranslation<
			{
				[p: string]: LocaleMessageValue<VueMessageType>;
			},
			string,
			RemoveIndexSignature<{
				[p: string]: LocaleMessageValue<VueMessageType>;
			}>,
			never,
			string,
			string
		>,
		runtimeStore: RuntimeStoreContext,
		router: Router,
		route: RouteLocationNormalizedLoaded,
		queryStore: QueryStore,
		commandBus: CommandBus,
		eventBus: EventBus,
		window: Window,
		splitViewEnabled: boolean
	) {
		this.t = t;
		this.runtimeStore = runtimeStore;
		this.router = router;
		this.route = route;
		this.queryStore = queryStore;
		this.commandBus = commandBus;
		this.eventBus = eventBus;
		this.window = window;
		this.splitViewEnabled = splitViewEnabled;
	}

	appHasMovedToTheBackground() {
		useAppStore().appHasMovedToTheBackground({
			browser: getBrowserName(),
			commandBus: this.commandBus,
			platform: getPlatformName(),
		});
	}

	appHasMovedToTheForeground() {
		this.eventBus.emit('warmupLists', {
			desiredDate: [formatDate(new Date(), 'YYYY-MM-DD')],
		});

		void useAlarmStore().fetchAlarms({
			localNotifications: LocalNotifications,
			queryStore: this.queryStore,
			t: this.t,
		});

		const userStateStore = useUserStateStore();
		void userStateStore.fetchAutomaticUserStates({
			queryStore: this.queryStore,
		});

		useAppStore().appHasMovedToTheForeground({
			browser: getBrowserName(),
			commandBus: this.commandBus,
			platform: getPlatformName(),
		});
	}

	cleanup() {
		if (this.alarmFetchInterval) {
			clearInterval(this.alarmFetchInterval);
		}

		if (this.updateTimeoutInterval) {
			clearInterval(this.updateTimeoutInterval);
		}
	}

	initializeTheApp(): void {
		this.watchTheDarkModeSetting();
		this.watchTheSplitViewEnabledSetting();

		void this.initializeTheAuthenticatedUser().then(() => {
			void this.registerPushNotifications();
			this.updateTheServiceWorkerRegistration();
			this.initializeTheUserOnlineListAndStates();
			this.initializeTheOrganizationTree();
			this.initializeTheIdoFavorites();
			this.initializeThePhoneCallGateway();
			this.initializeTheAlarmFetch();
			this.watchTheOutGoingCallConfigurationForTheUser();
			this.initializeFavoritesSynchronization();
		});

		initializeEventBusSubscriptions(this.eventBus, this.runtimeStore, this.queryStore, this.commandBus);

		loadingStatePlugin(this.route);
	}

	initializeTheIdoFavorites(): void {
		const organizationStore = useOrganizationStore();
		void organizationStore.fetchIdoFavorites({
			queryStore: this.queryStore,
		});
	}

	initializeFavoritesSynchronization(): void {
		const searchHistoryStore = useSearchHistoryStore();

		void searchHistoryStore
			.loadFromBackend({
				commandBus: this.commandBus,
				queryStore: this.queryStore,
			})
			.then(() => {
				void searchHistoryStore.migrateLocalstorageToBackend({
					commandBus: this.commandBus,
					queryStore: this.queryStore,
				});
			});
	}

	initializeThePhoneCallGateway(): void {
		const phoneCallStore = usePhoneCallStore();

		const phoneCallGatewayFactory = new PhoneCallGatewayFactory(
			axios,
			this.queryStore,
			this.commandBus,
			this.runtimeStore,
			phoneCallStore,
			this.router,
			this.route,
			this.eventBus,
			this.splitViewEnabled,
			this.t
		);

		void phoneCallGatewayFactory.createPhoneCallGateway().then((createdPhoneCallGateway) => {
			simpleObservableStore.phoneCallGateway = createdPhoneCallGateway;
		});
	}

	initializeTheOrganizationTree(): void {
		const organizationStore = useOrganizationStore();
		void organizationStore.fetchSharedQueries({
			queryStore: this.queryStore,
		});
	}

	initializeTheUserOnlineListAndStates(): void {
		const userOnlineListStore = useUserOnlineListStore();

		userOnlineListStore.fetchUserOnlineList({
			queryStore: this.queryStore,
		});

		userOnlineListStore.fetchUserStates({
			queryStore: this.queryStore,
		});
	}

	initializeTheAlarmFetch(): void {
		if (!isRunningInNativeCapacitorApp()) {
			this.alarmFetchInterval = setInterval(() => {
				void useAlarmStore().fetchAlarms({
					localNotifications: LocalNotifications,
					queryStore: this.queryStore,
					t: this.t,
				});
			}, 60000);
		}
	}

	registerPushNotifications() {
		this.fcmService = new FcmService(
			this.t,
			this.runtimeStore,
			this.router,
			this.route,
			this.queryStore,
			this.commandBus,
			this.eventBus,
			this.splitViewEnabled
		);
		this.fcmService.initPush();

		this.eventBus.on('logout', () => {
			if (this.fcmService !== undefined) {
				this.fcmService.unsubscribePush();
			}
		});
	}

	public setSplitViewEnabled(state: boolean) {
		this.splitViewEnabled = state;
		if (this.fcmService !== undefined) {
			this.fcmService.setSplitViewEnabled(state);
		}
	}

	updateTheServiceWorkerRegistration() {
		if (simpleObservableStore.registration && !simpleObservableStore.registration.waiting) {
			void simpleObservableStore.registration.update();
		}
	}

	watchTheDarkModeSetting() {
		watch(
			() => useUserInterfaceSettingsStore().themeMode,
			(themeMode) => {
				applyDarkMode(themeMode);
			},
			{ immediate: true }
		);
	}

	watchTheOutGoingCallConfigurationForTheUser() {
		const phoneCallStore = usePhoneCallStore();

		watch(
			() => phoneCallStore.outgoingCallConfigurationForTheUser,
			(outgoingCallConfigurationForTheUser: OutgoingCallConfigurationForTheUser | null) => {
				if (
					JSON.stringify(this.outgoingCallConfigurationForTheUser) !==
					JSON.stringify(outgoingCallConfigurationForTheUser)
				) {
					this.initializeThePhoneCallGateway();
					this.outgoingCallConfigurationForTheUser = outgoingCallConfigurationForTheUser;
				}
			}
		);
	}

	watchTheSplitViewEnabledSetting() {
		watch(
			() => useUserInterfaceSettingsStore().splitViewEnabled,
			(splitViewEnabled: boolean) => {
				this.setSplitViewEnabled(splitViewEnabled);
			},
			{ immediate: true }
		);
	}
}
